import type { IconName } from '../components/ui/Icon';

/**
 * Professional milestones derived from what the learner has actually done. Every milestone is
 * unlocked by real completions recorded on the server (scenarios, deliverables, labs, lessons,
 * interviews); nothing here is awarded for visits, streaks or time spent. Milestones describe
 * practice, never qualification.
 */
export interface AchievementFacts {
  completedScenarios: { scenarioId: string; careerId: string; score: number; completedAt: string; trainingMode?: string }[];
  deliverables: { deliverableType: string; createdAt: string }[];
  lessonsPassed: { lessonId: string; at: string }[];
  labsPassed: { labId: string; mode: string; completedAt?: string }[];
  interviews: { mode: string; status: string; completedAt?: string; overallScore?: number }[];
  tiers: { careerId: string; tier: string }[];
  careersPlayed?: number;
}

export interface Achievement {
  id: string;
  title: string;
  description: string;
  icon: IconName;
  /** Which part of the platform it comes from; used for grouping. */
  area: 'workplace' | 'learning' | 'career' | 'interview';
  unlocked: boolean;
  unlockedAt?: string;
  /** Progress toward the milestone when it is countable, 0..1. */
  progress?: number;
  /** Plain-language hint on what unlocks it. */
  howTo: string;
}

export const ACHIEVEMENT_NOTICE = 'Milestones record practice you have completed in WorkSim AI. They are not qualifications, certifications or endorsements.';

const earliest = (dates: (string | undefined)[]): string | undefined => dates.filter((d): d is string => Boolean(d)).sort()[0];
const nth = (dates: (string | undefined)[], n: number): string | undefined => dates.filter((d): d is string => Boolean(d)).sort()[n - 1];

interface Def { id: string; title: string; description: string; icon: IconName; area: Achievement['area']; howTo: string; evaluate: (f: AchievementFacts) => { unlocked: boolean; at?: string; progress?: number } }

const byType = (f: AchievementFacts, ...types: string[]) => f.deliverables.filter((d) => types.includes(d.deliverableType));
const byPrefix = (f: AchievementFacts, prefix: string) => f.completedScenarios.filter((s) => s.scenarioId.startsWith(prefix));
const count = (n: number, target: number) => ({ unlocked: n >= target, progress: Math.min(1, n / target) });

const DEFS: Def[] = [
  {
    id: 'first-scenario', title: 'First day done', description: 'Completed your first workplace scenario at TechCore.', icon: 'flag', area: 'workplace', howTo: 'Complete any scenario.',
    evaluate: (f) => ({ unlocked: f.completedScenarios.length > 0, at: earliest(f.completedScenarios.map((s) => s.completedAt)) }),
  },
  {
    id: 'first-ticket', title: 'First ticket resolved', description: 'Worked a service desk ticket through to a documented resolution.', icon: 'ticket', area: 'workplace', howTo: 'Submit a ticket resolution note in a scenario.',
    evaluate: (f) => { const d = byType(f, 'ticket-resolution-note'); return { unlocked: d.length > 0, at: earliest(d.map((x) => x.createdAt)) }; },
  },
  {
    id: 'requirements-gatherer', title: 'Requirements gatherer', description: 'Turned stakeholder conversations into a written requirements document.', icon: 'clipboard', area: 'workplace', howTo: 'Submit a requirements document in a scenario.',
    evaluate: (f) => { const d = byType(f, 'requirements-document'); return { unlocked: d.length > 0, at: earliest(d.map((x) => x.createdAt)) }; },
  },
  {
    id: 'data-quality-investigator', title: 'Data quality investigator', description: 'Found and explained problems in real-looking data with a report to back it up.', icon: 'search', area: 'workplace', howTo: 'Complete a Data Analyst scenario and submit its report.',
    evaluate: (f) => { const s = byPrefix(f, 'da-'); const d = byType(f, 'data-report'); return { unlocked: s.length > 0 && d.length > 0, at: earliest(d.map((x) => x.createdAt)) }; },
  },
  {
    id: 'customer-communication', title: 'Customer communication', description: 'Kept a customer informed with an honest update during a difficult situation.', icon: 'chat', area: 'workplace', howTo: 'Complete a Customer Success scenario.',
    evaluate: (f) => { const s = byPrefix(f, 'cs-'); return { unlocked: s.length > 0, at: earliest(s.map((x) => x.completedAt)) }; },
  },
  {
    id: 'independent', title: 'Independent scenario completed', description: 'Finished a scenario without guided hints, in independent or assessment mode.', icon: 'star', area: 'workplace', howTo: 'Complete a scenario in independent or assessment mode.',
    evaluate: (f) => { const s = f.completedScenarios.filter((x) => x.trainingMode === 'independent' || x.trainingMode === 'assessment'); return { unlocked: s.length > 0, at: earliest(s.map((x) => x.completedAt)) }; },
  },
  {
    id: 'strong-outcome', title: 'Strong outcome', description: 'Scored 85% or higher on a scenario evaluation.', icon: 'trophy', area: 'workplace', howTo: 'Reach 85% on any scenario.',
    evaluate: (f) => { const s = f.completedScenarios.filter((x) => x.score >= 0.85); return { unlocked: s.length > 0, at: earliest(s.map((x) => x.completedAt)) }; },
  },
  {
    id: 'cross-functional', title: 'Cross-functional', description: 'Worked a scenario that spans departments and handoffs.', icon: 'users', area: 'workplace', howTo: 'Complete a cross-functional scenario (ids start with xf-).',
    evaluate: (f) => { const s = byPrefix(f, 'xf-'); return { unlocked: s.length > 0, at: earliest(s.map((x) => x.completedAt)) }; },
  },
  {
    id: 'five-scenarios', title: 'Five scenarios', description: 'Completed five scenarios across your tracks.', icon: 'briefcase', area: 'workplace', howTo: 'Complete five scenarios.',
    evaluate: (f) => ({ ...count(f.completedScenarios.length, 5), at: nth(f.completedScenarios.map((s) => s.completedAt), 5) }),
  },
  {
    id: 'first-lesson', title: 'Studied up', description: 'Passed your first knowledge check.', icon: 'book', area: 'learning', howTo: 'Pass a lesson knowledge check.',
    evaluate: (f) => ({ unlocked: f.lessonsPassed.length > 0, at: earliest(f.lessonsPassed.map((l) => l.at)) }),
  },
  {
    id: 'hands-on', title: 'Hands on', description: 'Completed a lab in the simulated tools.', icon: 'terminal', area: 'learning', howTo: 'Pass any lab.',
    evaluate: (f) => ({ unlocked: f.labsPassed.length > 0, at: earliest(f.labsPassed.map((l) => l.completedAt)) }),
  },
  {
    id: 'challenge-passed', title: 'Challenge passed', description: 'Passed a lab in challenge mode, without hints or step-by-step guidance.', icon: 'lightbulb', area: 'learning', howTo: 'Pass a lab in challenge mode.',
    evaluate: (f) => { const l = f.labsPassed.filter((x) => x.mode === 'challenge'); return { unlocked: l.length > 0, at: earliest(l.map((x) => x.completedAt)) }; },
  },
  {
    id: 'ten-labs', title: 'Ten labs', description: 'Passed ten labs.', icon: 'terminal', area: 'learning', howTo: 'Pass ten labs.',
    evaluate: (f) => ({ ...count(f.labsPassed.length, 10), at: nth(f.labsPassed.map((l) => l.completedAt), 10) }),
  },
  {
    id: 'foundational', title: 'Foundational tier', description: 'Reached the foundational tier on a career track.', icon: 'chart', area: 'career', howTo: 'Demonstrate foundational skills in scenarios.',
    evaluate: (f) => ({ unlocked: f.tiers.some((t) => ['foundational', 'intermediate', 'advanced'].includes(t.tier)) }),
  },
  {
    id: 'intermediate', title: 'Intermediate tier', description: 'Reached the intermediate tier on a career track.', icon: 'chart', area: 'career', howTo: 'Keep demonstrating skills across scenarios.',
    evaluate: (f) => ({ unlocked: f.tiers.some((t) => ['intermediate', 'advanced'].includes(t.tier)) }),
  },
  {
    id: 'explorer', title: 'Explorer', description: 'Completed scenarios in two different career tracks.', icon: 'compass', area: 'career', howTo: 'Complete a scenario in a second track.',
    evaluate: (f) => { const n = new Set(f.completedScenarios.map((s) => s.careerId)).size; return count(n, 2); },
  },
  {
    id: 'mock-interview', title: 'Mock interview', description: 'Completed a full mock interview.', icon: 'target', area: 'interview', howTo: 'Master a learning path, then run a mock interview.',
    evaluate: (f) => { const s = f.interviews.filter((x) => x.mode === 'mock' && x.status === 'completed'); return { unlocked: s.length > 0, at: earliest(s.map((x) => x.completedAt)) }; },
  },
];

export function computeAchievements(facts: AchievementFacts): Achievement[] {
  return DEFS.map((d) => {
    const r = d.evaluate(facts);
    return { id: d.id, title: d.title, description: d.description, icon: d.icon, area: d.area, howTo: d.howTo, unlocked: r.unlocked, unlockedAt: r.unlocked ? r.at : undefined, progress: r.progress };
  });
}

/** Most recently unlocked first. */
export function recentAchievements(all: Achievement[], n = 3): Achievement[] {
  return all.filter((a) => a.unlocked).sort((a, b) => (b.unlockedAt ?? '').localeCompare(a.unlockedAt ?? '')).slice(0, n);
}

/** The next milestones worth showing: locked, with the most progress first. */
export function nextAchievements(all: Achievement[], n = 3): Achievement[] {
  return all.filter((a) => !a.unlocked).sort((a, b) => (b.progress ?? 0) - (a.progress ?? 0)).slice(0, n);
}
